import { isBrowser } from '@sbt-web/utils';

type NavigationType =
  | 'navigate'
  | 'reload'
  | 'back_forward'
  | 'prerender'
  | 'reserved';

const failOnServer = (): void => {
  if (!isBrowser) {
    throw new Error(
      'This function can only be called on the browser. Please add a check to your code.'
    );
  }
};

const getNavigationType = (): NavigationType => {
  failOnServer();

  // This path will work on Chrome and Firefox
  if ('getEntriesByType' in window.performance) {
    const navigationEvent =
      window.performance.getEntriesByType('navigation')[0];

    if (navigationEvent != null && 'type' in navigationEvent) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return navigationEvent.type;
    }
  }

  const legacyNavigationType = window.performance.navigation.type;

  switch (legacyNavigationType) {
    case window.performance.navigation.TYPE_NAVIGATE: {
      return 'navigate';
    }
    case window.performance.navigation.TYPE_RELOAD: {
      return 'reload';
    }
    case window.performance.navigation.TYPE_BACK_FORWARD: {
      return 'back_forward';
    }
    default: {
      return 'reserved';
    }
  }
};

/**
 * Determines if the current page load was triggered by standard navigation (e.g., clicking a link, entering a URL).
 *
 * @returns {boolean} True if the navigation type is 'navigate', otherwise false.
 */
const loadedFromNavigation = (): boolean => getNavigationType() === 'navigate';

/**
 * Determines if the current page load was triggered by a browser reload (e.g., pressing F5 or the reload button).
 *
 * @returns {boolean} True if the navigation type is 'reload', otherwise false.
 */
const loadedFromReload = (): boolean => getNavigationType() === 'reload';

/**
 * Determines if the current page load was triggered by a history navigation (e.g., using the browser's back or forward buttons).
 *
 * @returns {boolean} True if the navigation type is 'back_forward', otherwise false.
 */
const loadedFromHistoryNavigation = (): boolean =>
  getNavigationType() === 'back_forward';

export { loadedFromNavigation, loadedFromReload, loadedFromHistoryNavigation };
